// @flow
import { type Node } from 'react';
import styled from '@emotion/styled';
import {
  SPACING,
  typography,
  DEPTH,
  BORDER_RADIUS,
  color
} from '@tablekit/theme';
import { mediaQuery, css } from '@tablekit/utils';
import { LAPTOP_BREAKPOINT, MOBILE_BREAKPOINT } from 'utils/theme';
import columnWidth from 'utils/columnWidth';
import type { BlokComponentPropsType, TestimonialBlockType } from '../types';
import { EDITOR_VALUES, handleEditorValues } from '../PageContext';
import PersonImage, { SIZES } from '../PersonImage';
import Link from '../Link';

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

const LinkedWrapper = styled(Wrapper.withComponent(Link))`
  &,
  &:hover,
  &:visited {
    text-decoration: none;
  }
`;

const QuoteText = styled.div`
  ${(props) =>
    mediaQuery(
      {
        [MOBILE_BREAKPOINT]: 'L3',
        default: 'L6'
      },
      (level) => typography(level)(props)
    )(props)};
  font-style: italic;
  @media ${MOBILE_BREAKPOINT} {
    margin-top: ${SPACING.L4};
  }
`;

const QuoteMark = styled.div`
  ${(props) =>
    mediaQuery(
      {
        [MOBILE_BREAKPOINT]: 'L9',
        default: 'L10'
      },
      (level) => css`
        font-size: ${typography(level)(props).fontSize};
        line-height: ${typography(level)(props).fontSize};
      `
    )(props)};
  display: flex;
  align-self: flex-start;
  color: ${color('accent')};
  margin: 0 ${SPACING.L4};

  @media ${MOBILE_BREAKPOINT} {
    position: absolute;
    top: ${SPACING.L4};
    left: ${SPACING.L4};
    margin: 0;
  }
`;

const QuoteAuthor = styled.div`
  flex: 1 1 100%;
  ${mediaQuery(
    {
      [MOBILE_BREAKPOINT]: 'L1',
      default: 'L3'
    },

    (level) => typography(level)
  )};
  margin-top: ${SPACING.L4};
  align-self: flex-end;
  text-align: right;
`;

const QuoteAfterMark = styled.div`
  display: flex;
  flex-direction: column;
`;

const QuoteContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-radius: ${BORDER_RADIUS}px;
  background-color: ${color('surface')};
  ${DEPTH.FLOAT};
  padding: ${SPACING.L4};
  align-items: center;

  @media ${LAPTOP_BREAKPOINT} {
    flex-direction: row;
    max-width: ${columnWidth(8)}%;
  }
`;

export default function ({
  blok: { content: blockContent }
}: BlokComponentPropsType<TestimonialBlockType>): Node {
  let content = blockContent;
  if (CONFIG.storyblok.version === 'draft') {
    content = handleEditorValues(EDITOR_VALUES.TESTIMONIAL, blockContent);
  }
  if (!content) {
    return <Wrapper>Referenced Testimonial is incorrect or deleted</Wrapper>;
  }
  const { image, client, quote, link } = content;
  let WrapperElement = Wrapper;
  const wrapperProps = {};
  if (link) {
    WrapperElement = LinkedWrapper;
    wrapperProps.link = link;
  }
  return (
    <WrapperElement {...wrapperProps}>
      <QuoteContent>
        <PersonImage
          src={image}
          alt={client}
          size={{
            [MOBILE_BREAKPOINT]: SIZES.TESTIMONIAL_MOBILE,
            default: SIZES.TESTIMONIAL
          }}
        />
        <QuoteMark>&ldquo;</QuoteMark>
        <QuoteAfterMark>
          <QuoteText>{quote}</QuoteText>
          <QuoteAuthor>{client}</QuoteAuthor>
        </QuoteAfterMark>
      </QuoteContent>
    </WrapperElement>
  );
}
